"use client";
import { useEffect, type ReactElement } from "react";
import { ErrorState } from "@/components/ErrorState";

type Props = {
  error: Error & { digest?: string };
  reset: () => void;
};

/**
 * The App Router boundary for every segment under /book.
 *
 * This is a public page a prospect opens from an email, with nothing else on
 * screen to fall back to: a render-time throw anywhere in the booking or manage
 * flow — a zone Intl rejects, a response shaped differently than the types say
 * — would otherwise leave them looking at a white rectangle. Next renders this
 * in place of the crashed segment, so the lead sees the same calm panel every
 * other failure on this page uses, and `reset` re-mounts the segment rather
 * than asking them to find the link again.
 */
export default function BookingError({ error, reset }: Props): ReactElement {
  useEffect(() => {
    // One choke point for reporting. Nothing is wired up on this app yet, and
    // the console is what a support engineer can actually be walked to over the
    // phone while the lead still has the tab open.
    console.error("[scheduler] unhandled error", error);
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-white p-6">
      <div className="w-full max-w-md">
        <ErrorState errorCode="UNEXPECTED_ERROR" onRetry={reset} />
      </div>
    </div>
  );
}
